import { useMemo, useState } from 'react';
import { format, isBefore, isValid, parse, startOfToday } from 'date-fns';
import {
  Box,
  Button,
  ButtonGroup,
  Combobox,
  type ComboboxOptionType,
  DateInput,
  DatePicker,
  Drawer,
  DropdownSelect,
  FormField,
  Heading,
  Icon,
  Label,
  Modal,
  Paragraph,
  Toast,
  VisuallyHidden,
  useToast,
} from '@zillow/constellation-10';
import { IconClockFilled } from '@zillow/constellation-icons';
import { convertDateToLocalDateTime } from '@zg-rentals/touring';
import StringConstants from '../../../constants/StringConstants';
import useDevices from '../../../hooks/useDevices';
import useScheduleTour from '../../../hooks/useScheduleTour';
import { getErrorDisplayMessage } from '../../../utils/errorUtils';

const DATE_FORMAT = 'MM/dd/yyyy';
const TIME_FORMAT = 'h:mm a';
const MINUTES_PER_TIME_OPTION = 30;
const TIME_OPTIONS = Array.from({ length: (24 * 60) / MINUTES_PER_TIME_OPTION }, (_, index) =>
  format(new Date(2000, 0, 1, 0, index * MINUTES_PER_TIME_OPTION), TIME_FORMAT),
);
const FALLBACK_ERROR_MESSAGE = 'Tour couldn’t be added. Check the time and try again.';

type AddManualTourModalProps = {
  conversationId: string;
  /** A tour time the landlord already noted for this conversation; prefills the form when it's still ahead. */
  initialStartTimeMs?: number;
  onClose: () => void;
};

const getInitialValues = (initialStartTimeMs?: number) => {
  if (!initialStartTimeMs || initialStartTimeMs <= Date.now()) {
    return { date: '', time: '' };
  }
  return { date: format(initialStartTimeMs, DATE_FORMAT), time: format(initialStartTimeMs, TIME_FORMAT) };
};

const parseDate = (date: string) => parse(date, DATE_FORMAT, new Date());

const isSelectableDate = (date: string) => {
  const parsedDate = parseDate(date);
  return isValid(parsedDate) && !isBefore(parsedDate, startOfToday());
};

export default function AddManualTourModal({ conversationId, initialStartTimeMs, onClose }: AddManualTourModalProps) {
  const { isMdAndBelow } = useDevices();
  const { enqueueToast } = useToast();
  const { mutateAsync: scheduleTour, isPending } = useScheduleTour();
  const [initialValues] = useState(() => getInitialValues(initialStartTimeMs));
  const [date, setDate] = useState(initialValues.date);
  const [time, setTime] = useState(initialValues.time);

  // A noted time can fall between the half-hour options, and the dropdown renders blank for a value it doesn't list.
  const timeOptions = useMemo(() => {
    if (!initialValues.time || TIME_OPTIONS.includes(initialValues.time)) {
      return TIME_OPTIONS;
    }
    const notedTime = parse(initialValues.time, TIME_FORMAT, new Date());
    const insertionIndex = Math.ceil((notedTime.getHours() * 60 + notedTime.getMinutes()) / MINUTES_PER_TIME_OPTION);
    return [...TIME_OPTIONS.slice(0, insertionIndex), initialValues.time, ...TIME_OPTIONS.slice(insertionIndex)];
  }, [initialValues.time]);

  const canSubmit = isSelectableDate(date) && !!time && !isPending;

  const handleDateChange = (value: Date | string) => {
    setDate(typeof value === 'string' ? value : format(value, DATE_FORMAT));
  };

  const handleTimeSelect = (option: ComboboxOptionType) => {
    if (typeof option === 'string') {
      setTime(option);
    }
  };

  const handleSubmit = async () => {
    const startDate = parse(`${date} ${time}`, `${DATE_FORMAT} ${TIME_FORMAT}`, new Date());
    try {
      await scheduleTour({ postData: { conversationId, startTime: convertDateToLocalDateTime(startDate) } });
      enqueueToast(<Toast tone="success">Tour added to calendar and booking notification sent.</Toast>);
      onClose();
    } catch (error) {
      // z-calendar's 4xx rejections (conflict, existing tour, past time, no Zillow account) are written for landlords.
      enqueueToast(<Toast tone="critical">{getErrorDisplayMessage(error, FALLBACK_ERROR_MESSAGE)}</Toast>);
    }
  };

  const heading = (
    <Heading level={2} textStyle="heading-xs">
      {isMdAndBelow ? 'Add tour to calendar' : 'Add a tour to calendar'}
    </Heading>
  );

  const body = (
    <Box css={{ display: 'flex', flexDirection: 'column', gap: 'default' }}>
      <Paragraph textStyle="body-sm">{StringConstants.ADD_TOUR_MODAL_DESCRIPTION}</Paragraph>
      <FormField
        // Figma's date trigger is neutral; Constellation's default calendar IconButton is brand-toned.
        css={{ '& .c11n_date-picker__trigger': { color: 'icon.neutral!' } }}
        label={
          <VisuallyHidden asChild>
            <Label>Tour date</Label>
          </VisuallyHidden>
        }
        control={
          <DatePicker
            closeModalOnDateSelect={!isMdAndBelow}
            dateFormat={DATE_FORMAT}
            input={<DateInput placeholder="MM/DD/YYYY" />}
            minDate={startOfToday()}
            onValueChange={handleDateChange}
            value={date}
          />
        }
      />
      <FormField
        label={
          <VisuallyHidden asChild>
            <Label>Tour time</Label>
          </VisuallyHidden>
        }
        control={
          <DropdownSelect
            options={timeOptions}
            onOptionSelect={handleTimeSelect}
            placeholder="12:00 PM"
            value={time}
            renderAdornment={(adornmentProps) => {
              const { ref: _ref, ...adornmentPropsWithoutRef } = adornmentProps;
              return (
                <Combobox.Adornment {...adornmentPropsWithoutRef}>
                  {/* ! outranks Combobox.Adornment's descendant `.c11n_icon` color rule */}
                  <Icon size="md" css={{ color: 'icon.neutral!' }} render={<IconClockFilled />} />
                </Combobox.Adornment>
              );
            }}
          />
        }
      />
    </Box>
  );

  const submitButton = (
    <Button disabled={!canSubmit} emphasis="filled" fluid={isMdAndBelow} onClick={handleSubmit} tone="brand">
      Add to calendar
    </Button>
  );

  if (isMdAndBelow) {
    return (
      <Drawer.Root open={true} onOpenChange={(open) => !open && onClose()} placement="bottom">
        <Drawer.Portal>
          <Drawer.Backdrop scrim>
            <Drawer.Content>
              <Drawer.Header>{heading}</Drawer.Header>
              <Drawer.Body>{body}</Drawer.Body>
              <Drawer.Footer>{submitButton}</Drawer.Footer>
              <Drawer.CloseButton />
            </Drawer.Content>
          </Drawer.Backdrop>
        </Drawer.Portal>
      </Drawer.Root>
    );
  }

  return (
    <Modal
      open={true}
      onOpenChange={(open) => !open && onClose()}
      size="sm"
      header={heading}
      body={body}
      footer={
        <ButtonGroup aria-label="Add tour actions">
          <Button emphasis="bare" onClick={onClose} tone="brand">
            Cancel
          </Button>
          {submitButton}
        </ButtonGroup>
      }
    />
  );
}
